import { useQuery } from '@tanstack/react-query';
import { DocumentReferencesView } from 'api';
import {
  DOCUMENTS_BY_ID_QUERY_KEY,
  DOCUMENTS_BY_ID_STALE_TIME,
} from 'common/constants/services/documents';
import FilesService from 'services/documents';
import { Logger } from 'utilities';
import { DocumentsById, GetDocumentsByIdReturn, UseGetDocumentsByIdParams } from './useGetDocumentsById.types';

const filesService = new FilesService();

const getDocumentsById = async (
  documentIds: string[],
  onDocumentLoadError?: (failedDocumentId: string) => void
): Promise<DocumentsById> => {
  if (!documentIds.length) {
    return {};
  }

  const results = await Promise.allSettled(
    documentIds.map(async id => {
      try {
        const doc = await filesService.getDocumentById(id);
        return { id, doc: doc as DocumentReferencesView };
      } catch (error) {
        Logger.error(`Error loading document ${id}`, error);
        throw new Error(id);
      }
    })
  );

  const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failures.length) {
    failures.forEach(f => onDocumentLoadError?.(f.reason?.message));
  }

  return results
    .filter(
      (r): r is PromiseFulfilledResult<{ id: string; doc: DocumentReferencesView }> => r.status === 'fulfilled'
    )
    .reduce<DocumentsById>((acc, { value: { id, doc } }) => {
      acc[id] = doc;
      return acc;
    }, {});
};

export default function useGetDocumentsById({
  documentIds,
  enabled = true,
  onDocumentLoadError,
}: UseGetDocumentsByIdParams): GetDocumentsByIdReturn {
  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<DocumentsById, Error>({
    queryKey: [DOCUMENTS_BY_ID_QUERY_KEY, documentIds],
    queryFn: () => getDocumentsById(documentIds, onDocumentLoadError),
    enabled: enabled && documentIds.length > 0,
    staleTime: DOCUMENTS_BY_ID_STALE_TIME,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
}
